// BkosApprovePage — /bkos/approve/:token. Thread mct_1f244f7e-1e4; Michael's
// yes 22 Oct 2026 (mca_ce65e9b8a68b).
//
// A BKOS firm asked for more clients and already has an Extra Clients
// subscription. The owner's email links here. Opening the page charges
// nothing (email scanners "click" links): it shows the exact charge, from the
// Engine, and only the Approve button on this page charges the card on file.
// The owner can pick a bigger number, up to the plan's top. If today's figure
// moved since the page opened, the Engine charges nothing and sends the new one.
// Never a Spiffy link.

import { useEffect, useState, type CSSProperties } from 'react';
import { useParams } from 'react-router-dom';
import TakerShell from './components/TakerShell';

const API = import.meta.env.VITE_API_BASE_URL as string;

interface Option { clients: number; extra_clients: number; per_period_cents: number; increase_cents: number; today_cents: number }
interface PageData {
  state: string;
  clients_to?: number | null;
  firm_name?: string | null;
  plan_name?: string;
  clients_from?: number;
  default_clients?: number;
  currency?: string;
  gst_included?: boolean;
  plus_tax?: boolean;
  cadence?: string;
  renewal?: string | null;
  asked_by?: string | null;
  options?: Option[];
}

const h1: CSSProperties = { fontFamily: "'Fraunces', serif", fontSize: 34, fontWeight: 900, color: '#0A0A2E', lineHeight: 1.1, margin: '0 0 12px' };
const lead: CSSProperties = { fontSize: 17, color: '#44445e', lineHeight: 1.55, margin: '0 0 20px' };
const card: CSSProperties = { background: '#fff', borderRadius: 16, padding: '20px 20px 22px', boxShadow: '0 4px 24px rgba(10,10,46,0.06)', marginBottom: 18 };
const button: CSSProperties = { background: '#F05A28', color: '#fff', border: 'none', borderRadius: 10, padding: '14px 22px', fontSize: 17, fontWeight: 700, cursor: 'pointer', width: '100%' };

function dayWords(value: string | null | undefined): string {
  if (!value) return 'your next renewal';
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00Z` : value.replace(' ', 'T'));
  return Number.isFinite(d.getTime())
    ? d.toLocaleDateString('en-US', { day: 'numeric', month: 'long', timeZone: 'UTC' })
    : 'your next renewal';
}

export default function BkosApprovePage() {
  const { token = '' } = useParams();
  const [data, setData] = useState<PageData | null>(null);
  const [failed, setFailed] = useState(false);
  const [pick, setPick] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [done, setDone] = useState<{ clients: number | null } | null>(null);

  const url = `${API}/api/public/bkos-plan-approve/${encodeURIComponent(token)}`;
  useEffect(() => {
    fetch(url)
      .then(async (r) => { if (!r.ok) throw new Error(String(r.status)); return r.json() as Promise<PageData> })
      .then((d) => { setData(d); setPick(d.default_clients ?? null); })
      .catch(() => setFailed(true));
  }, [url]);

  const money = (cents: number) => `$${(cents / 100).toFixed(2)} ${data?.currency ?? ''}`.trim();
  const gst = data?.gst_included ? ' incl. GST' : data?.plus_tax ? ' plus tax' : '';
  const per = data?.cadence === 'annual' ? 'year' : 'month';
  const chosen = data?.options?.find((o) => o.clients === pick) ?? null;

  const approve = async () => {
    if (!chosen || busy) return;
    setBusy(true); setMessage('');
    try {
      const r = await fetch(url, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clients: chosen.clients, today_cents: chosen.today_cents }),
      });
      const d = await r.json().catch(() => null) as { ok?: boolean; error?: string; clients_to?: number; page?: PageData } | null;
      if (r.ok && d?.ok) { setDone({ clients: d.clients_to ?? chosen.clients }); return; }
      if (d?.page) { setData(d.page); }
      setMessage(d?.error ?? 'Something went wrong. Nothing was charged. Please try again, or reply to our email.');
    } catch {
      // The answer was lost, which is not proof nothing happened: in the 26 Oct
      // live test the charge went through while the page said it could not
      // reach the server. Ask the Engine what became of the request before
      // saying anything (a charge in progress shows as paying for a few seconds).
      for (let i = 0; i < 6; i++) {
        try {
          const r = await fetch(url);
          const d = r.ok ? await r.json() as PageData : null;
          if (d && (d.state === 'applied' || d.state === 'paid')) { setDone({ clients: d.clients_to ?? chosen.clients }); return; }
          if (d && d.state !== 'paying') break;
        } catch { /* try again */ }
        await new Promise((res) => setTimeout(res, 3000));
      }
      setMessage("We couldn't confirm that just now. Please don't press Approve again. You'll get a receipt by email within a few minutes if it went through, or reply to our email and we'll check for you.");
    } finally {
      setBusy(false);
    }
  };

  const closed = data && data.state !== 'open';
  return (
    <TakerShell footerLabel="BKOS by The Successful Bookkeeper">
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '40px 16px 80px' }}>
        {failed && (
          <>
            <h1 style={h1}>This link isn't working</h1>
            <p style={lead}>It may have expired. Ask again from BKOS, or reply to our email and we'll sort it out. Nothing was charged.</p>
          </>
        )}
        {!failed && !data && <p style={lead}>Loading…</p>}
        {done && (
          <>
            <h1 style={h1}>Done</h1>
            <p style={lead}>Your workspace now takes up to {done.clients} clients. A receipt is on its way to your inbox.</p>
          </>
        )}
        {!done && closed && (data!.state === 'applied' || data!.state === 'paid' || data!.state === 'paying') && (
          <>
            <h1 style={h1}>Already approved</h1>
            <p style={lead}>This request has gone through{data!.clients_to ? `: your workspace takes up to ${data!.clients_to} clients` : ''}. Nothing more to do here.</p>
          </>
        )}
        {!done && closed && !['applied', 'paid', 'paying'].includes(data!.state) && (
          <>
            <h1 style={h1}>{data!.state === 'expired' ? 'This link has expired' : 'This request is closed'}</h1>
            <p style={lead}>Nothing was charged. If you still want more clients, ask again from BKOS and we'll send a fresh email.</p>
          </>
        )}
        {!done && data && data.state === 'open' && chosen && (
          <>
            <h1 style={h1}>More clients for {data.firm_name ?? 'your BKOS workspace'}</h1>
            <p style={lead}>
              {data.asked_by ? `${data.asked_by} asked for more room in BKOS. ` : ''}
              Your {data.plan_name} plan takes {data.clients_from} clients today.
            </p>
            <div style={card}>
              <label style={{ display: 'block', fontSize: 15, fontWeight: 600, color: '#0A0A2E', marginBottom: 8 }} htmlFor="bkos-clients">
                How many clients in total?
              </label>
              <select
                id="bkos-clients" value={pick ?? ''} onChange={(e) => setPick(Number(e.target.value))}
                style={{ fontSize: 17, padding: '10px 12px', borderRadius: 8, border: '1px solid #ccd', width: '100%', marginBottom: 16 }}
              >
                {data.options!.map((o) => (
                  <option key={o.clients} value={o.clients}>{o.clients} clients ({o.clients - (data.clients_from ?? 0)} more)</option>
                ))}
              </select>
              <p style={{ ...lead, fontSize: 16, margin: '0 0 8px' }}>
                <strong>Today: {money(chosen.today_cents)}{gst}</strong> for the rest of this {per}.
              </p>
              <p style={{ ...lead, fontSize: 16, margin: '0 0 18px' }}>
                Then your BKOS plan goes up by {money(chosen.increase_cents)}{gst} a {per} from your next renewal on {dayWords(data.renewal)}.
                Your card on file is charged, and your renewal date doesn't change.
              </p>
              <button style={{ ...button, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={() => void approve()}>
                {busy ? 'Approving…' : `Approve and pay ${money(chosen.today_cents)}${gst}`}
              </button>
              {message && <p style={{ color: '#B91C1C', fontSize: 15, margin: '12px 0 0' }}>{message}</p>}
            </div>
            <p style={{ fontSize: 14, color: '#6b6b80' }}>If you didn't ask for this, close this page and nothing changes.</p>
          </>
        )}
      </div>
    </TakerShell>
  );
}
